<div class="flex min-h-screen items-center justify-center px-4 py-8">
  <div class="flex w-full max-w-md flex-col gap-6">
    <app-logo [widthInPx]="120" />

    <div hlmCard>
      <div hlmCardHeader>
        <h1 hlmCardTitle>
          {{
            verbundInviteToken && !inviteError
              ? 'Als Mitglied registrieren'
              : 'Kostenlos registrieren'
          }}
        </h1>
        <p hlmCardDescription>
          Digitale Geschenkgutscheine in wenigen Minuten einrichten – ohne Fixkosten.
        </p>
      </div>

      <div hlmCardContent class="space-y-6">
        @if (inviteError) {
          <div hlmAlert variant="destructive">
            <ng-icon name="lucideTriangleAlert" />
            <p hlmAlertTitle>{{ inviteError }}</p>
            <p hlmAlertDescription>
              Bitte die Zentrale um einen neuen Link. Ohne Link registrierst du einen eigenen
              Gutschein-Shop.
            </p>
          </div>
        } @else if (verbundInviteToken) {
          <div hlmAlert>
            <ng-icon name="lucideUsers" />
            <p hlmAlertTitle>
              Registrierung als Mitglied{{ pendingGroupName ? ' von ' + pendingGroupName : '' }}
            </p>
            <p hlmAlertDescription>
              Du löst Gutscheine des Netzwerks bei dir ein, und die Zentrale überweist dir deine
              Einlösungen einmal im Monat. Nach der Registrierung hinterlegst du deine
              Bankverbindung, dann gibt dich die Zentrale frei.
            </p>
          </div>
        } @else if (referrer) {
          <div hlmAlert role="status">
            <ng-icon name="lucideGift" />
            <p hlmAlertTitle>{{ referrer.name }} hat dir Schenkwerk empfohlen</p>
            <p hlmAlertDescription>
              Starte jetzt mit deinem eigenen Gutschein-Shop: Als Empfehlung zahlst du nach dem
              Start {{ referrer.welcome_months }} Monate lang nur
              {{ formatRate(referrer.welcome_fee_rate) }} statt
              {{ formatRate(referrer.default_fee_rate) }} Gebühr.
              <a
                href="https://schenkwerk.com/empfehlungsprogramm/teilnahmebedingungen"
                target="_blank"
                rel="noopener"
                class="underline underline-offset-4"
                >Teilnahmebedingungen</a
              >
            </p>
          </div>
        } @else if (pendingGroupName) {
          <div hlmAlert>
            <ng-icon name="lucideUsers" />
            <p hlmAlertTitle>Einladung zu {{ pendingGroupName }}</p>
            <p hlmAlertDescription>Registriere dich, um dem Netzwerk beizutreten.</p>
          </div>
        }

        <form [formGroup]="registerForm" (ngSubmit)="onSubmit()" class="flex flex-col gap-6">
          <div hlmField>
            <label hlmFieldLabel for="email">E-Mail-Adresse</label>
            <input
              hlmInput
              id="email"
              type="email"
              inputmode="email"
              formControlName="email"
              autocomplete="email"
              placeholder="name@beispiel.de"
              class="w-full"
              [attr.aria-invalid]="
                (submitted || f['email'].touched) && f['email'].errors ? 'true' : null
              " />
            @if ((submitted || f['email'].touched) && f['email'].errors) {
              <p hlmFieldDescription class="text-destructive">
                @if (f['email'].errors['required']) {
                  Die E-Mail-Adresse ist verpflichtend
                } @else if (f['email'].errors['email']) {
                  Bitte gib eine gültige E-Mail-Adresse an
                } @else {
                  {{ f['email'].errors['serverError'] }}
                }
              </p>
            }
          </div>

          <div hlmField>
            <label hlmFieldLabel for="password">Passwort</label>
            <hlm-input-group class="w-full">
              <input
                hlmInputGroupInput
                id="password"
                [type]="showPassword ? 'text' : 'password'"
                formControlName="password"
                autocomplete="new-password"
                placeholder="Mindestens 8 Zeichen"
                [attr.aria-invalid]="submitted && f['password'].errors ? 'true' : null"
                (focus)="onPasswordFocus()" />
              <hlm-input-group-addon align="inline-end">
                <button
                  hlmInputGroupButton
                  type="button"
                  [attr.aria-label]="showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'"
                  (click)="togglePasswordVisibility()">
                  <ng-icon [name]="showPassword ? 'lucideEyeOff' : 'lucideEye'" />
                </button>
              </hlm-input-group-addon>
            </hlm-input-group>

            @if (passwordFocused && !allPasswordRequirementsMet) {
              <ul class="mt-1 space-y-1">
                @for (requirement of passwordRequirements; track requirement.key) {
                  <li
                    class="flex items-center gap-1.5 text-xs"
                    [class.text-muted-foreground]="!isPasswordRequirementMet(requirement.key)"
                    [class.text-foreground]="isPasswordRequirementMet(requirement.key)">
                    <ng-icon
                      [name]="
                        isPasswordRequirementMet(requirement.key) ? 'lucideCheck' : 'lucideDot'
                      "
                      class="shrink-0" />
                    {{ requirement.label }}
                  </li>
                }
              </ul>
            }

            @if (submitted && f['password'].errors) {
              <p hlmFieldDescription class="text-destructive">
                @if (f['password'].errors['required']) {
                  Das Passwort ist erforderlich
                } @else if (f['password'].errors['minlength']) {
                  Das Passwort muss mindestens 8 Zeichen haben.
                } @else if (f['password'].errors['pattern']) {
                  Das Passwort braucht einen Groß-, einen Kleinbuchstaben und eine Zahl.
                } @else {
                  {{ f['password'].errors['serverError'] }}
                }
              </p>
            }
          </div>

          <div hlmField>
            <label hlmFieldLabel for="confirmPassword">Passwort bestätigen</label>
            <hlm-input-group class="w-full">
              <input
                hlmInputGroupInput
                id="confirmPassword"
                [type]="showConfirmPassword ? 'text' : 'password'"
                formControlName="confirmPassword"
                autocomplete="new-password"
                placeholder="Passwort wiederholen"
                [attr.aria-invalid]="
                  submitted &&
                  (f['confirmPassword'].errors || registerForm.errors?.['passwordsNotMatching'])
                    ? 'true'
                    : null
                " />
              <hlm-input-group-addon align="inline-end">
                <button
                  hlmInputGroupButton
                  type="button"
                  [attr.aria-label]="
                    showConfirmPassword ? 'Passwort verbergen' : 'Passwort anzeigen'
                  "
                  (click)="toggleConfirmPasswordVisibility()">
                  <ng-icon [name]="showConfirmPassword ? 'lucideEyeOff' : 'lucideEye'" />
                </button>
              </hlm-input-group-addon>
            </hlm-input-group>

            @if (submitted && f['confirmPassword'].errors) {
              <p hlmFieldDescription class="text-destructive">
                @if (f['confirmPassword'].errors['required']) {
                  Bitte bestätige dein Passwort.
                } @else {
                  {{ f['confirmPassword'].errors['serverError'] }}
                }
              </p>
            } @else if (submitted && registerForm.errors?.['passwordsNotMatching']) {
              <p hlmFieldDescription class="text-destructive">
                Die Passwörter stimmen nicht überein.
              </p>
            }
          </div>

          <div hlmField>
            <label class="flex cursor-pointer items-start gap-2">
              <hlm-checkbox
                class="mt-0.5"
                formControlName="termsAccepted"
                aria-label="AGB und Datenschutzerklärung akzeptieren" />
              <span class="text-sm">
                Ich habe die
                <a
                  href="https://schenkwerk.com/agbs"
                  target="_blank"
                  class="underline underline-offset-4"
                  >AGB</a
                >
                sowie die
                <a
                  href="https://schenkwerk.com/datenschutz"
                  target="_blank"
                  class="underline underline-offset-4"
                  >Datenschutzerklärung</a
                >
                gelesen und akzeptiere sie.
              </span>
            </label>
            @if (submitted && f['termsAccepted'].errors) {
              <p hlmFieldDescription class="text-destructive">
                Bitte akzeptiere die AGB und Datenschutzerklärung
              </p>
            }
          </div>

          @if (error) {
            <div hlmAlert variant="destructive">
              <ng-icon name="lucideTriangleAlert" />
              <p hlmAlertDescription [innerHTML]="error"></p>
            </div>
          }

          <div class="flex flex-col gap-4">
            <button hlmBtn type="submit" class="w-full" [disabled]="loading">
              @if (loading) {
                <hlm-spinner />
                Konto wird erstellt…
              } @else {
                Weiter
              }
            </button>

            <div [hidden]="!googleAvailable" class="flex flex-col gap-4">
              <div
                class="after:border-border relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t">
                <span hlmMuted class="bg-card relative z-10 px-2">oder</span>
              </div>

              <!-- Google's own button, rendered by GIS. It has to stay visible and
                   unobscured: behind an overlay or opacity trick Chrome rejects the
                   FedCM credential request, so the click does nothing. -->
              <div #googleBtn class="flex justify-center [&>*]:max-w-full"></div>

              <p hlmMuted class="text-xs">
                Mit der Registrierung über Google akzeptierst du unsere
                <a
                  href="https://schenkwerk.com/agbs"
                  target="_blank"
                  class="underline underline-offset-4"
                  >AGB</a
                >
                und
                <a
                  href="https://schenkwerk.com/datenschutz"
                  target="_blank"
                  class="underline underline-offset-4"
                  >Datenschutzerklärung</a
                >.
              </p>
            </div>
          </div>
        </form>

        <p class="text-center text-sm">
          Du hast bereits einen Account?
          <a
            routerLink="/login"
            [queryParams]="{ redirect: redirectAfterRegister, groupName: pendingGroupName }"
            class="underline underline-offset-4">
            Login
          </a>
        </p>
      </div>
    </div>

    <div class="text-muted-foreground flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs">
      <span class="flex items-center gap-1"
        ><ng-icon name="lucideShieldCheck" /> DSGVO-konform</span
      >
      <span class="flex items-center gap-1"><ng-icon name="lucideLock" /> SSL-verschlüsselt</span>
      <a
        href="https://stripe.com/de"
        target="_blank"
        rel="noopener noreferrer"
        class="hover:text-foreground flex items-center gap-1 transition-colors">
        <ng-icon name="lucideCreditCard" />
        Zahlung über Stripe
      </a>
    </div>
  </div>
</div>
